import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import ScannerPage from './components/ScannerPage';
import ResultPage from './components/ResultPage';
import LoginPage from './components/LoginPage';
import DashboardHome from './components/DashboardHome';
import PurchaseTicketPage from './components/PurchaseTicketPage';
import SellTicketPage from './components/SellTicketPage';
import EventManagementPage from './components/EventManagementPage';
import TransferTicketPage from './components/TransferTicketPage';
import BulkUploadPage from './components/BulkUploadPage';
import UserManagementPage from './components/UserManagementPage';
import TeamManagementPage from './components/TeamManagementPage';
import FeedbackManagementPage from './components/FeedbackManagementPage';
import AdminManagementPage from './components/AdminManagementPage';
import FeatureManagementPage from './components/FeatureManagementPage';
import EmailVerificationPage from './components/EmailVerificationPage';
import UserVerificationPage from './components/UserVerificationPage';
import RefundManagementPage from './components/RefundManagementPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import AuditPage from './components/AuditPage';
import PaymentAuditPage from './components/PaymentAuditPage';
import ContentManagementPage from './components/ContentManagementPage';
import BundleManagementPage from './components/BundleManagementPage';
import CategoryTagManagementPage from './components/CategoryTagManagementPage';
import FeatureGateManagementPage from './components/FeatureGateManagementPage';
import SubscriptionManagementPage from './components/SubscriptionManagementPage';
import AmwayToolPage from './components/AmwayToolPage';
import ContentAssignPage from './components/ContentAssignPage';
import ContentPaymentAuditPage from './components/ContentPaymentAuditPage';
import ContentSubscriptionAuditPage from './components/ContentSubscriptionAuditPage';
import UserAuditPage from './components/UserAuditPage';
import ContentAnalyticsPage from './components/ContentAnalyticsPage';
import IboSpendingReportPage from './components/IboSpendingReportPage';
import BannerManagementPage from './components/BannerManagementPage';
import NotificationTemplatesPage from './components/NotificationTemplatesPage';
import NotificationCategoriesPage from './components/NotificationCategoriesPage';
import MassCommunicationPage from './components/MassCommunicationPage';
import ComplianceMessagesPage from './components/ComplianceMessagesPage';
import FoundationalEducationPage from './components/FoundationalEducationPage';
import UserHierarchyPage from './components/UserHierarchyPage';
import AuthorManagementPage from './components/AuthorManagementPage';
import BookManagementPage from './components/BookManagementPage';
import BookBundleManagementPage from './components/BookBundleManagementPage';
import WarehouseManagementPage from './components/WarehouseManagementPage';
import ShippingManagementPage from './components/ShippingManagementPage';
import TaxRateManagementPage from './components/TaxRateManagementPage';
import BookOrderManagementPage from './components/BookOrderManagementPage';
import AdminLayout from './components/layout/AdminLayout';
import './App.css';
import { TICKET_TRANSFER_ENABLED } from './config/ticketTransfer';

function App() {
  const [scanResult, setScanResult] = useState('');
  const [selectedEventId, setSelectedEventId] = useState(null);
  
  const handleScan = (data, eventId) => {
    if (data) {
      setScanResult(data);
      setSelectedEventId(eventId);
    }
  };

  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes - no layout shell */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />

          {/* Protected routes - wrapped in AdminLayout */}
          <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<DashboardHome />} />
            <Route path="scanner" element={<ScannerPage onScan={handleScan} />} />
            <Route path="result" element={<ResultPage result={scanResult} eventId={selectedEventId} />} />
            <Route path="purchase" element={<PurchaseTicketPage />} />
            <Route path="sell" element={<SellTicketPage />} />
            <Route path="events" element={<EventManagementPage />} />
            {/* DV-312: admin ticket transfer — see config/ticketTransfer.js. */}
            {TICKET_TRANSFER_ENABLED && (
              <Route path="transfer" element={<TransferTicketPage />} />
            )}
            <Route path="audit" element={<AuditPage />} />
            <Route path="payment-audit" element={<PaymentAuditPage />} />
            <Route path="user-audit" element={<UserAuditPage />} />
            <Route path="bulk-upload" element={<BulkUploadPage />} />
            <Route path="user-management" element={<UserManagementPage />} />
            <Route path="team-management" element={<TeamManagementPage />} />
            <Route path="feedback-management" element={<FeedbackManagementPage />} />
            <Route path="admin-management" element={<AdminManagementPage />} />
            <Route path="feature-management" element={<FeatureManagementPage />} />
            <Route path="user-verification" element={<UserVerificationPage />} />
            <Route path="refund-management" element={<RefundManagementPage />} />
            <Route path="content-management" element={<ContentManagementPage />} />
            <Route path="bundle-management" element={<BundleManagementPage />} />
            <Route path="category-tag-management" element={<CategoryTagManagementPage />} />
            <Route path="feature-gates" element={<FeatureGateManagementPage />} />
            <Route path="subscription-management" element={<SubscriptionManagementPage />} />
            <Route path="amway-tool" element={<AmwayToolPage />} />
            <Route path="content-assign" element={<ContentAssignPage />} />
            <Route path="content/payment-audit" element={<ContentPaymentAuditPage />} />
            <Route path="content/subscription-audit" element={<ContentSubscriptionAuditPage />} />
            <Route path="content/analytics" element={<ContentAnalyticsPage />} />
            <Route path="ibo-spending-report" element={<IboSpendingReportPage />} />
            <Route path="banner-management" element={<BannerManagementPage />} />
            <Route path="notification-templates" element={<NotificationTemplatesPage />} />
            <Route path="notification-categories" element={<NotificationCategoriesPage />} />
            <Route path="mass-communication" element={<MassCommunicationPage />} />
            <Route path="compliance-messages" element={<ComplianceMessagesPage />} />
            <Route path="foundational-education" element={<FoundationalEducationPage />} />
            <Route path="user-hierarchy" element={<UserHierarchyPage />} />
            <Route path="author-management" element={<AuthorManagementPage />} />
            <Route path="book-management" element={<BookManagementPage />} />
            <Route path="book-bundle-management" element={<BookBundleManagementPage />} />
            <Route path="warehouse-management" element={<WarehouseManagementPage />} />
            <Route path="shipping-management" element={<ShippingManagementPage />} />
            <Route path="tax-rate-management" element={<TaxRateManagementPage />} />
            <Route path="book-order-management" element={<BookOrderManagementPage />} />
          </Route>

          {/* Redirect any unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
